/* eslint-disable no-use-before-define */

const { REACT_APP_PROXY_HOST } = process.env;
const URL_GRAPHE_COGNITIF_STATIQUE = '/projections-documentaires/graphe-cognitif.json';
const URL_GRAPHE_COGNITIF_VIVANT = (REACT_APP_PROXY_HOST || '') + '/monitoring/graphe/cognitif/projection';
const URL_ACTIONS_COGNITIVES = (REACT_APP_PROXY_HOST || '') + '/monitoring/graphe/cognitif/actions';
let cacheProjectionGrapheCognitif = null;
let chargementProjectionGrapheCognitif = null;

export const ID_ACTION_BRANCHE_DEPUIS_CONVERSATION = 'branche-vivante-depuis-conversation';
export const ID_ACTION_TRAVAILLER_FRONTIERE = 'travailler-frontiere';
export const POIDS_PILOTAGE_DEFAUT = {
  valeur: 2,
  pression: 2,
  energie: 1,
  chaleur: 1,
};

export async function chargerGrapheCognitif(options = {}) {
  const forcer = Boolean(options && options.forcer);
  if (!forcer && cacheProjectionGrapheCognitif) return cacheProjectionGrapheCognitif;
  if (!forcer && chargementProjectionGrapheCognitif) return chargementProjectionGrapheCognitif;

  chargementProjectionGrapheCognitif = fetchJsonAvecRepli(URL_GRAPHE_COGNITIF_VIVANT, URL_GRAPHE_COGNITIF_STATIQUE)
    .then((payload) => {
      cacheProjectionGrapheCognitif = construireProjectionGrapheCognitif(payload);
      chargementProjectionGrapheCognitif = null;
      return cacheProjectionGrapheCognitif;
    })
    .catch((erreur) => {
      chargementProjectionGrapheCognitif = null;
      throw erreur;
    });

  return chargementProjectionGrapheCognitif;
}

export function lireProjectionGrapheCognitifEnCache() {
  return cacheProjectionGrapheCognitif;
}

export function oublierProjectionGrapheCognitifEnCache() {
  cacheProjectionGrapheCognitif = null;
  chargementProjectionGrapheCognitif = null;
}

export async function creerActionChangementEtatBranche(branche, valeurDemandee) {
  const actionId = construireActionId(branche && branche.id, valeurDemandee);
  const reponse = await fetch(URL_ACTIONS_COGNITIVES, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action_id: actionId,
      branche_id: branche && branche.id,
      type_action: 'changer_etat_branche',
      valeur_demandee: valeurDemandee,
    }),
  });
  const texte = await reponse.text();
  const payload = texte ? JSON.parse(texte) : {};
  if (!reponse.ok) {
    throw new Error((payload && payload.message) || ('HTTP ' + reponse.status));
  }
  return normaliserActionHumaine(payload.action);
}

export async function chargerActionsBranche(brancheId) {
  const url = URL_ACTIONS_COGNITIVES + '?statut=disponible,prise_en_charge,appliquee,refusee';
  const reponse = await fetch(url, { method: 'GET', cache: 'no-store' });
  if (!reponse.ok) return [];
  const payload = await reponse.json();
  return (payload.actions || [])
    .map(normaliserActionHumaine)
    .filter((action) => action.branche_id === brancheId);
}

export function construireProjectionGrapheCognitif(payload) {
  const domaines = Array.isArray(payload && payload.domaines) ? payload.domaines : [];
  const branches = Array.isArray(payload && payload.branches) ? payload.branches : [];
  const domainesParId = domaines.reduce((index, domaine) => {
    if (domaine && domaine.id) index[domaine.id] = domaine;
    return index;
  }, {});

  const branchesNormalisees = branches.map((branche) => normaliserBranche(branche, domainesParId));
  const branchesParId = branchesNormalisees.reduce((index, branche) => {
    index[branche.id] = branche;
    return index;
  }, {});

  const domainesProjetes = domaines.map((domaine) => {
    const branchesAncrees = branchesNormalisees.filter((branche) => branche.ancrage.id === domaine.id);
    const branchesLiees = branchesNormalisees.filter((branche) => branche.ancrage.id !== domaine.id
      && branche.domaines_lies.indexOf(domaine.id) >= 0);

    return {
      ...domaine,
      branchesAncrees,
      branchesLiees,
      visibleParDefaut: branchesAncrees.length > 0 || branchesLiees.length > 0,
    };
  });

  return {
    schemaVersion: payload && payload.schema_version,
    projection: (payload && payload.projection) || {},
    actionsCognitives: normaliserActionsCognitives(payload && payload.actions_cognitives),
    domaines: domainesProjetes,
    branches: branchesNormalisees,
    branchesParId,
    pilotage: normaliserPilotage(payload && payload.pilotage, branchesParId),
    resume: {
      nbDomaines: domaines.length,
      nbBranches: branchesNormalisees.length,
      nbBranchesTransverses: branchesNormalisees.filter((branche) => branche.estTransverse).length,
      nbPreuvesConnues: branchesNormalisees.filter((branche) => branche.prochaine_preuve).length,
      nbFrontieresActives: branchesNormalisees.reduce((total, branche) => total + branche.frontieres_actives.length, 0),
      nbMouvementsCandidats: branchesNormalisees.reduce((total, branche) => (
        total + branche.chemins_attention.nb_mouvements_candidats
      ), 0),
      nbBranchesChaudes: branchesNormalisees.filter((branche) => (
        branche.chaleur_experimentale.niveau === 'chaud'
        || branche.chaleur_experimentale.niveau === 'tres_chaud'
      )).length,
    },
  };
}

export function trouverActionCognitive(projection, actionId) {
  if (!projection || !Array.isArray(projection.actionsCognitives)) return null;
  return projection.actionsCognitives.find((action) => action.id === actionId) || null;
}

export function construirePromptActionCognitive(action, options = {}) {
  if (!action || !action.contrat) return '';
  const contexteBranche = (options.contexteBranche || '').trim();

  const lignes = [
    `# ${action.titre}`,
    '',
    'Vous etes dans la conversation ChatGPT qui contient le contexte du chantier a capturer.',
    '',
    "A partir de cette conversation et du contrat ci-dessous, produisez directement le prompt complet destine a l'agent d'admission SyncStats System.",
    '',
    "La reponse attendue doit etre un prompt Codex complet. Elle ne doit pas supposer que l'utilisateur reconstruira un deuxieme prompt.",
    '',
  ];

  if (contexteBranche) {
    lignes.push(
      '## Contexte canonique deja connu de la branche source',
      '',
      contexteBranche,
      '',
    );
  }

  lignes.push(
    '## Contrat versionne SyncStats System',
    '',
    action.contrat.trim(),
    '',
    '## Sortie attendue',
    '',
    'Produisez uniquement le prompt Codex complet, en incluant la proposition semantique extraite de cette conversation.',
  );

  return lignes.join('\n');
}

export function construirePromptTravaillerFrontiere(action, branche, frontiere) {
  if (!action || !action.contrat || !branche || !frontiere) return '';

  const contexteBranche = construireContexteReprise(branche).trim();
  const contexteFrontiere = construireContexteFrontiere(frontiere).trim();
  if (!contexteBranche || !contexteFrontiere) return '';

  return [
    '# Travailler une Frontiere SyncStats',
    '',
    'Ce texte est autosuffisant et destine a ChatGPT ou a une IA conversationnelle capable de raisonnement general.',
    'Aucun appel LLM, aucune inspection de repo et aucune mutation du graphe ne sont effectues depuis Monitoring.',
    '',
    '## Contrat versionne SyncStats System',
    '',
    action.contrat.trim(),
    '',
    '## Contexte de la Branche',
    '',
    contexteBranche,
    '',
    '## Frontiere a travailler',
    '',
    contexteFrontiere,
    '',
    '## Travail demande',
    '',
    "Analysez cette Frontiere dans le contexte fourni, puis proposez une progression raisonnable pour la travailler.",
    "Ne forcez pas un plan d'implementation si la bonne prochaine etape est une analyse, une verification, une decision humaine ou une collecte de preuve.",
  ].join('\n');
}

export function prochainEtatBasculable(branche) {
  if (!branche) return null;
  if (branche.etat_general === 'active') return 'en_pause';
  if (branche.etat_general === 'en_pause') return 'active';
  return null;
}

export function libelleStatutAction(statut) {
  const libelles = {
    disponible: 'En attente',
    prise_en_charge: 'Prise en charge',
    appliquee: 'Appliquee',
    refusee: 'Refusee',
  };
  return libelles[statut] || statut;
}

export function recalculerRecommandationsPilotage(recommandations, poids = POIDS_PILOTAGE_DEFAUT) {
  if (!Array.isArray(recommandations)) return [];
  return recommandations
    .map((recommandation, index) => {
      const scorePilotage = calculerScorePilotage(recommandation, poids);
      return {
        ...recommandation,
        scorePilotage,
        scorePilotageOriginal: index,
      };
    })
    .sort((a, b) => {
      const ecartScore = b.scorePilotage.score - a.scorePilotage.score;
      if (Math.abs(ecartScore) > 0.0001) return ecartScore;
      return a.scorePilotageOriginal - b.scorePilotageOriginal;
    });
}

export function filtrerRecommandationsPilotage(recommandations, filtres = {}) {
  if (!Array.isArray(recommandations)) return [];
  return recommandations.filter((recommandation) => {
    const domaine = normaliserFiltre(filtres.domaine);
    const acteur = normaliserFiltre(filtres.acteur);
    const typeTravail = normaliserFiltre(filtres.typeTravail);
    const domaines = domainesRecommandationPilotage(recommandation).map((item) => item.id);

    return (!domaine || domaines.indexOf(domaine) >= 0)
      && (!acteur || normaliserFiltre(recommandation && recommandation.acteurPotentiel) === acteur)
      && (!typeTravail || normaliserFiltre(recommandation && recommandation.typeTravail) === typeTravail);
  });
}

export function optionsFiltresPilotage(recommandations, domainesProjetes = []) {
  const domainesParId = domainesProjetes.reduce((index, domaine) => {
    if (domaine && domaine.id) index[domaine.id] = domaine;
    return index;
  }, {});
  const domaines = new Map();
  const acteurs = new Set();
  const typesTravail = new Set();

  (Array.isArray(recommandations) ? recommandations : []).forEach((recommandation) => {
    domainesRecommandationPilotage(recommandation).forEach((domaine) => {
      if (!domaine.id) return;
      const domaineProjete = domainesParId[domaine.id];
      domaines.set(domaine.id, {
        id: domaine.id,
        titre: (domaineProjete && domaineProjete.titre) || domaine.titre || domaine.id,
      });
    });
    if (recommandation && recommandation.acteurPotentiel) acteurs.add(recommandation.acteurPotentiel);
    if (recommandation && recommandation.typeTravail) typesTravail.add(recommandation.typeTravail);
  });

  return {
    domaines: Array.from(domaines.values()).sort(comparerTitre),
    acteurs: Array.from(acteurs).sort().map((id) => ({ id, titre: libelleValeurCognitive(id) })),
    typesTravail: Array.from(typesTravail).sort().map((id) => ({ id, titre: libelleValeurCognitive(id) })),
  };
}

export function domainesRecommandationPilotage(recommandation) {
  if (!recommandation) return [];
  const branche = recommandation.branche || {};
  const domainesBranche = [
    branche.ancrage && branche.ancrage.id,
  ].concat(Array.isArray(branche.domaines_lies) ? branche.domaines_lies : []);
  const domaines = recommandation.transversalite && Array.isArray(recommandation.transversalite.domaines)
    && recommandation.transversalite.domaines.length > 0
    ? recommandation.transversalite.domaines
    : domainesBranche;
  const indexTitres = {};
  if (branche.ancrage && branche.ancrage.id) indexTitres[branche.ancrage.id] = branche.ancrage.titre || branche.ancrage.id;
  (branche.domainesLiesObjets || []).forEach((domaine) => {
    if (domaine && domaine.id) indexTitres[domaine.id] = domaine.titre || domaine.id;
  });
  return Array.from(new Set((domaines || []).filter(Boolean))).map((id) => ({
    id,
    titre: indexTitres[id] || id,
  }));
}

export function calculerScorePilotage(recommandation, poids = POIDS_PILOTAGE_DEFAUT) {
  const criteres = [
    {
      id: 'valeur',
      libelle: 'Valeur',
      poids: poids.valeur,
      score: scoreSignalContinuOuNiveau(recommandation && recommandation.valeur, {
        faible: 0.25,
        moyenne: 0.6,
        forte: 0.9,
        tres_forte: 1,
        critique: 1,
      }),
    },
    {
      id: 'pression',
      libelle: 'Pression',
      poids: poids.pression,
      score: scoreSignalContinuOuNiveau(recommandation && recommandation.pression, {
        faible: 0.25,
        moyenne: 0.6,
        forte: 0.9,
        tres_forte: 1,
        critique: 1,
      }),
    },
    {
      id: 'energie',
      libelle: 'Faible energie',
      poids: poids.energie,
      score: scoreNiveau(recommandation && recommandation.energie && recommandation.energie.niveau, {
        faible: 1,
        moyenne: 0.6,
        forte: 0.15,
        elevee: 0.15,
      }),
    },
    {
      id: 'chaleur',
      libelle: 'Chaleur',
      poids: poids.chaleur,
      score: scoreSignalContinuOuNiveau(recommandation && recommandation.chaleur, {
        froid: 0.25,
        tiede: 0.6,
        tres_chaud: 1,
        chaud: 1,
      }),
    },
  ].filter((critere) => Number(critere.poids) > 0);

  const disponibles = criteres.filter((critere) => critere.score !== null);
  const poidsTotal = disponibles.reduce((total, critere) => total + Number(critere.poids), 0);
  const score = poidsTotal > 0
    ? disponibles.reduce((total, critere) => total + (critere.score * Number(critere.poids)), 0) / poidsTotal
    : 0;

  return {
    score,
    scorePourcent: Math.round(score * 100),
    contributions: disponibles.map((critere) => ({
      id: critere.id,
      libelle: critere.libelle,
      poids: Number(critere.poids),
      scorePourcent: Math.round(critere.score * 100),
    })),
    inconnus: criteres
      .filter((critere) => critere.score === null)
      .map((critere) => critere.libelle),
  };
}

function scoreNiveau(niveau, table) {
  if (!niveau) return null;
  if (Object.prototype.hasOwnProperty.call(table, niveau)) return table[niveau];
  return null;
}

function scoreSignalContinuOuNiveau(signal, table) {
  if (!signal) return null;
  const scoreMachine = signal.valeur_machine_v0
    && signal.valeur_machine_v0.score !== undefined
    && signal.valeur_machine_v0.score !== null
    ? signal.valeur_machine_v0.score
    : null;
  const valeurContinue = scoreMachine !== null
    ? scoreMachine
    : signal.valeur_continue !== undefined && signal.valeur_continue !== null
    ? signal.valeur_continue
    : signal.chaleur_continue;
  if (estNombreFourni(valeurContinue)) {
    return Math.max(0, Math.min(1, Number(valeurContinue) / 100));
  }
  if (estNombreFourni(signal.valeur)) {
    return Math.max(0, Math.min(1, Number(signal.valeur) / 100));
  }
  return scoreNiveau(signal.niveau, table);
}

function estNombreFourni(valeur) {
  return valeur !== undefined
    && valeur !== null
    && valeur !== ''
    && Number.isFinite(Number(valeur));
}

function normaliserFiltre(valeur) {
  const texte = String(valeur || '').trim();
  return !texte || texte === 'tous' ? '' : texte;
}

function comparerTitre(a, b) {
  return String(a.titre || a.id).localeCompare(String(b.titre || b.id), 'fr', { sensitivity: 'base' });
}

function construireActionId(brancheId, valeurDemandee) {
  const maintenant = new Date().toISOString().replace(/[^0-9A-Za-z]/g, '').slice(0, 15);
  return ['monitoring', brancheId || 'branche', 'etat', valeurDemandee || 'inconnu', maintenant].join('-');
}

async function fetchJsonAvecRepli(urlPrimaire, urlRepli) {
  try {
    const reponse = await fetch(urlPrimaire, { method: 'GET', cache: 'no-store' });
    if (reponse.ok) return reponse.json();
  } catch (erreurPrimaire) {
    // Le fichier statique garde le cockpit lisible tant que PROD n'a pas encore recu la projection vivante.
  }

  const reponseRepli = await fetch(urlRepli, { method: 'GET', cache: 'no-store' });
  if (!reponseRepli.ok) {
    throw new Error(`HTTP ${reponseRepli.status} - ${urlRepli}`);
  }
  return reponseRepli.json();
}

function normaliserActionHumaine(action) {
  const source = action || {};
  return {
    action_id: source.action_id || '',
    branche_id: source.branche_id || '',
    type_action: source.type_action || '',
    valeur_demandee: source.valeur_demandee || '',
    statut: source.statut || 'inconnu',
    demande_at: source.demande_at || null,
    prise_en_charge_at: source.prise_en_charge_at || null,
    resultat_at: source.resultat_at || null,
    message_resultat: source.message_resultat || '',
  };
}
export function cheminBrancheVivante(brancheId, options = {}) {
  const chemin = `/cockpit/graphe-cognitif/${encodeURIComponent(brancheId || '')}`;
  if (!options.frontiereId) return chemin;
  return `${chemin}?frontiere=${encodeURIComponent(options.frontiereId)}`;
}

export function trouverBrancheVivante(projection, brancheId) {
  if (!projection || !projection.branchesParId || !brancheId) return null;
  return projection.branchesParId[brancheId] || null;
}

export function construireContexteReprise(branche) {
  if (!branche) return '';

  const lignes = [
    'BRANCHE',
    branche.titre || 'Inconnue',
    '',
    'DATE DE CREATION',
    branche.date_creation || 'Inconnue',
    '',
    'ETAT',
    libelleValeurCognitive(branche.etat_general),
    '',
    'ANCRAGE',
    (branche.ancrage && branche.ancrage.titre) || 'Inconnu',
    '',
    'DOMAINES TOUCHES',
    listePourContexte(branche.domainesLiesObjets, (domaine) => domaine.titre, 'Aucun autre domaine'),
    '',
    'TAGS',
    listePourContexte(branche.tags, (tag) => tag, 'Aucun tag'),
    '',
    'PROCHAINE PREUVE',
    branche.prochaine_preuve || 'Inconnue',
    '',
    'REPRISE',
    libelleValeurCognitive(branche.energie_activation),
    '',
    'SOLIDITE DES PREUVES',
    libelleValeurCognitive(branche.solidite_preuves),
  ];

  ajouterSectionTexte(lignes, 'RESUME DE RECONNAISSANCE', branche.resume_reconnaissance);
  ajouterSectionReferences(lignes, 'REFERENCES UTILES', branche.references_utiles);
  ajouterSectionFrontieres(lignes, 'OU POUSSER MAINTENANT', branche.frontieres_candidates);
  ajouterSectionTexte(lignes, 'POURQUOI CETTE BRANCHE EXISTE', branche.memoire_reprise.raison_existence);
  ajouterSectionTexte(lignes, 'CONTEXTE DE REPRISE', branche.memoire_reprise.contexte_utile);
  ajouterSectionTexte(lignes, 'ETAT ACTUEL CONNU', branche.memoire_reprise.etat_actuel);
  ajouterSectionListe(lignes, 'FAITS ETABLIS', branche.memoire_reprise.faits_etablis);
  ajouterSectionListe(lignes, 'DECISIONS ACQUISES', branche.memoire_reprise.decisions_acquises);
  ajouterSectionListe(lignes, 'PREUVES CONNUES', branche.memoire_reprise.preuves_connues);
  ajouterSectionListe(lignes, 'TRAVAIL SUIVANT CONNU', branche.memoire_reprise.travail_suivant_connu);
  ajouterSectionListe(lignes, 'QUESTIONS OUVERTES', branche.memoire_reprise.questions_ouvertes);
  ajouterSectionListe(lignes, 'INCERTITUDES A PRESERVER', branche.memoire_reprise.elements_incertains);
  ajouterSectionListe(lignes, 'ARTEFACTS IMPORTANTS', branche.memoire_reprise.artefacts_mentionnes);
  ajouterSectionListe(lignes, 'INTERPRETATIONS UTILES NON DECISIONNELLES', branche.memoire_reprise.interpretations);
  ajouterSectionListe(lignes, 'PROPOSITIONS NON DECIDEES', branche.memoire_reprise.propositions);
  ajouterSectionListe(lignes, 'FRONTIERES CANDIDATES NARRATIVES', branche.memoire_reprise.frontieres_candidates);

  return lignes.join('\n');
}

export function libelleValeurCognitive(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return 'Inconnu';
  const texte = String(valeur);
  const libelles = {
    non_evaluee: 'Non evaluee',
    inconnue: 'Inconnue',
    a_confirmer: 'A confirmer',
    active: 'Active',
    en_pause: 'En pause',
    faible: 'Faible',
    archivee: 'Archivee',
    inconnu: 'Inconnu',
  };
  return libelles[texte] || texte.replace(/_/g, ' ');
}

function listePourContexte(items, itemLabel, vide) {
  if (!items || items.length === 0) return vide;
  return items.map((item) => `- ${itemLabel(item)}`).join('\n');
}

function ajouterSectionTexte(lignes, titre, valeur) {
  if (!valeur) return;
  lignes.push('', titre, valeur);
}

function ajouterSectionListe(lignes, titre, items) {
  if (!Array.isArray(items) || items.length === 0) return;
  lignes.push('', titre, listePourContexte(items, (item) => item, ''));
}

function ajouterSectionReferences(lignes, titre, items) {
  if (!Array.isArray(items) || items.length === 0) return;
  lignes.push('', titre);
  items.forEach((reference) => {
    lignes.push(`- ${reference.titre}${reference.type ? ` (${reference.type})` : ''}: ${reference.cible}`);
  });
}

function ajouterSectionFrontieres(lignes, titre, items) {
  if (!Array.isArray(items) || items.length === 0) return;
  lignes.push('', titre);
  items.forEach((frontiere, index) => {
    lignes.push(`${index + 1}. ${frontiere.objectif_question}`);
    if (frontiere.id) lignes.push(`   Identifiant: ${frontiere.id}`);
    if (frontiere.etat_connu) lignes.push(`   Etat connu: ${frontiere.etat_connu}`);
    if (frontiere.avancee_candidate) lignes.push(`   Avancee candidate: ${frontiere.avancee_candidate}`);
    if (frontiere.type_mouvement_experimental && frontiere.type_mouvement_experimental !== 'inconnu') {
      lignes.push(`   Type de mouvement experimental: ${frontiere.type_mouvement_experimental}`);
    }
    if (frontiere.acteur_potentiel_experimental && frontiere.acteur_potentiel_experimental !== 'inconnu') {
      lignes.push(`   Acteur potentiel experimental: ${frontiere.acteur_potentiel_experimental}`);
    }
    if (frontiere.preuve_attendue) lignes.push(`   Preuve attendue: ${frontiere.preuve_attendue}`);
    if (frontiere.blocage_question) lignes.push(`   Blocage/question: ${frontiere.blocage_question}`);
    if (frontiere.energie_activation) lignes.push(`   Energie d'activation: ${frontiere.energie_activation}`);
  });
}

export function construireContexteFrontiere(frontiere) {
  if (!frontiere) return '';
  const lignes = [];
  ajouterSectionTexte(lignes, 'IDENTIFIANT', frontiere.id);
  ajouterSectionTexte(lignes, 'OBJECTIF / QUESTION', frontiere.objectif_question);
  ajouterSectionTexte(lignes, 'ETAT CONNU', frontiere.etat_connu);
  ajouterSectionTexte(lignes, 'AVANCEE CANDIDATE', frontiere.avancee_candidate);
  if (frontiere.type_mouvement_experimental && frontiere.type_mouvement_experimental !== 'inconnu') {
    ajouterSectionTexte(lignes, 'TYPE DE MOUVEMENT EXPERIMENTAL', frontiere.type_mouvement_experimental);
  }
  if (frontiere.acteur_potentiel_experimental && frontiere.acteur_potentiel_experimental !== 'inconnu') {
    ajouterSectionTexte(lignes, 'ACTEUR POTENTIEL EXPERIMENTAL', frontiere.acteur_potentiel_experimental);
  }
  ajouterSectionTexte(lignes, 'PREUVE ATTENDUE', frontiere.preuve_attendue);
  ajouterSectionTexte(lignes, 'BLOCAGE / QUESTION', frontiere.blocage_question);
  ajouterSectionTexte(lignes, 'ENERGIE D ACTIVATION', frontiere.energie_activation);
  return lignes.join('\n');
}

function normaliserActionsCognitives(actions) {
  if (!Array.isArray(actions)) return [];
  return actions
    .filter((action) => action && action.id)
    .map((action) => ({
      id: action.id,
      contratId: action.contrat_id || '',
      version: action.version || null,
      titre: action.titre || action.id,
      source: action.source || '',
      contrat: action.contrat || '',
    }));
}

function normaliserBranche(branche, domainesParId) {
  const ancrage = (branche && branche.ancrage) || {};
  const domainesLies = Array.isArray(branche && branche.domaines_lies) ? branche.domaines_lies : [];
  const tags = Array.isArray(branche && branche.tags) ? branche.tags : [];
  const domaineAncrage = domainesParId[ancrage.id] || { id: ancrage.id || 'inconnu', titre: ancrage.id || 'Inconnu' };
  const domainesLiesObjets = domainesLies.map((domaineId) => domainesParId[domaineId] || { id: domaineId, titre: domaineId });
  const frontieres = frontieresCandidates(branche.frontieres_candidates || branche.frontieres_actives);
  const frontieresActives = frontieresCandidates(branche.frontieres_actives || branche.frontieres_candidates);

  return {
    id: branche.id,
    titre: branche.titre,
    ancrage: {
      type: ancrage.type || 'domaine',
      id: ancrage.id,
      titre: domaineAncrage.titre,
    },
    domaines_lies: domainesLies,
    domainesLiesObjets,
    tags,
    etat_general: branche.etat_general || 'inconnu',
    prochaine_preuve: branche.prochaine_preuve || null,
    energie_activation: branche.energie_activation || 'inconnue',
    solidite_preuves: branche.solidite_preuves || 'inconnue',
    date_creation: branche.date_creation || null,
    resume_reconnaissance: branche.resume_reconnaissance || '',
    references_utiles: referencesUtiles(branche.references_utiles),
    frontieres_candidates: frontieres,
    frontieres_actives: frontieresActives,
    chemins_attention: normaliserCheminsAttention(branche.chemins_attention, frontieresActives),
    signaux_attention: normaliserSignauxAttention(branche.signaux_attention),
    chaleur_experimentale: normaliserChaleur(branche.chaleur_experimentale || (branche.signaux_attention && branche.signaux_attention.chaleur)),
    pression_observee: normaliserSignalSimple(branche.pression_observee, 'non_disponible'),
    recence_experimentale: normaliserRecence(branche.recence_experimentale || (branche.signaux_attention && branche.signaux_attention.recence)),
    transversalite_experimentale: normaliserTransversalite(
      branche.transversalite_experimentale || (branche.signaux_attention && branche.signaux_attention.transversalite),
      [domaineAncrage.id].concat(domainesLies),
    ),
    memoire_reprise: normaliserMemoireReprise(branche.memoire_reprise),
    estTransverse: domainesLies.length > 0,
  };
}

function normaliserMemoireReprise(memoire) {
  const source = memoire || {};
  return {
    raison_existence: source.raison_existence || '',
    contexte_utile: source.contexte_utile || '',
    etat_actuel: source.etat_actuel || '',
    faits_etablis: liste(source.faits_etablis),
    interpretations: liste(source.interpretations),
    propositions: liste(source.propositions),
    decisions_acquises: liste(source.decisions_acquises),
    preuves_connues: liste(source.preuves_connues),
    travail_suivant_connu: liste(source.travail_suivant_connu),
    questions_ouvertes: liste(source.questions_ouvertes),
    artefacts_mentionnes: liste(source.artefacts_mentionnes),
    elements_incertains: liste(source.elements_incertains),
    frontieres_candidates: liste(source.frontieres_candidates),
  };
}

function liste(valeur) {
  return Array.isArray(valeur) ? valeur.filter((item) => item !== null && item !== undefined && item !== '') : [];
}

function referencesUtiles(valeur) {
  if (!Array.isArray(valeur)) return [];
  return valeur
    .filter((item) => item && item.titre && item.cible)
    .map((item) => ({
      titre: item.titre,
      cible: item.cible,
      type: item.type || '',
    }));
}

function frontieresCandidates(valeur) {
  if (!Array.isArray(valeur)) return [];
  return valeur
    .filter((item) => item && item.objectif_question)
    .map((item) => ({
      id: item.id || '',
      identite_stable: item.identite_stable || null,
      objectif_question: item.objectif_question,
      etat_connu: item.etat_connu || '',
      avancee_candidate: item.avancee_candidate || '',
      mouvement_candidat: item.mouvement_candidat || item.avancee_candidate || '',
      type_mouvement_experimental: item.type_mouvement_experimental || 'inconnu',
      acteur_potentiel_experimental: item.acteur_potentiel_experimental || 'inconnu',
      preuve_attendue: item.preuve_attendue || '',
      blocage_question: item.blocage_question || '',
      energie_activation: item.energie_activation || '',
      energie_activation_machine_v0: normaliserEnergieActivationMachine(item.energie_activation_machine_v0),
      energie_activation_effective: normaliserEnergieActivationSignal(item.energie_activation_effective),
    }));
}

function normaliserCheminsAttention(source, frontieres) {
  const valeur = source || {};
  const frontieresActives = Array.isArray(frontieres) ? frontieres : [];
  const mouvementsCandidats = frontieresActives.filter((frontiere) => frontiere.mouvement_candidat).length;
  const preuvesAttendues = frontieresActives.filter((frontiere) => frontiere.preuve_attendue).length;
  return {
    nb_frontieres_actives: Number(valeur.nb_frontieres_actives || frontieresActives.length || 0),
    nb_mouvements_candidats: Number(valeur.nb_mouvements_candidats || mouvementsCandidats || 0),
    nb_preuves_attendues: Number(valeur.nb_preuves_attendues || preuvesAttendues || 0),
  };
}

function normaliserSignauxAttention(source) {
  const valeur = source || {};
  return {
    chaleur: normaliserChaleur(valeur.chaleur),
    recence: normaliserRecence(valeur.recence),
    transversalite: normaliserTransversalite(valeur.transversalite, []),
    pression: normaliserSignalSimple(valeur.pression, 'non_disponible'),
    valeur: normaliserValeurMachineSignal(valeur.valeur),
  };
}


function normaliserPilotage(source, branchesParId) {
  const valeur = source || {};
  const recommandations = Array.isArray(valeur.recommandations) ? valeur.recommandations : [];
  const candidats = Array.isArray(valeur.candidats) ? valeur.candidats : recommandations;
  return {
    type: valeur.type || 'pilotage-attentionnel-v0',
    statut: valeur.statut || 'experimental',
    rappel: valeur.rappel || '',
    selection: valeur.selection || {},
    candidats: candidats
      .map((candidat) => normaliserRecommandationPilotage(candidat, branchesParId))
      .filter((candidat) => candidat && candidat.branche),
    recommandations: recommandations
      .map((recommandation) => normaliserRecommandationPilotage(recommandation, branchesParId))
      .filter((recommandation) => recommandation && recommandation.branche),
    nbCandidatsFrontieres: Number(valeur.nb_candidats_frontieres || 0),
    limiteRecommandations: Number(valeur.limite_recommandations || 5),
    conventionsVisuelles: valeur.conventions_visuelles || {},
  };
}

function normaliserRecommandationPilotage(recommandation, branchesParId) {
  const source = recommandation || {};
  const brancheId = source.branche && source.branche.id;
  const branche = branchesParId[brancheId];
  if (!branche) return null;
  const frontiereId = source.frontiere && source.frontiere.id;
  const frontiere = branche.frontieres_actives.find((item) => item.id === frontiereId)
    || branche.frontieres_candidates.find((item) => item.id === frontiereId)
    || null;
  const domainesParDefaut = [branche.ancrage && branche.ancrage.id]
    .concat(branche.domaines_lies || [])
    .filter(Boolean);
  return {
    categorie: source.categorie || 'chemin_a_examiner',
    branche,
    frontiere,
    frontiereId: frontiereId || '',
    objectifFrontiere: (source.frontiere && source.frontiere.objectif_question) || (frontiere && frontiere.objectif_question) || '',
    typeTravail: source.type_travail || 'inconnu',
    acteurPotentiel: source.acteur_potentiel || 'inconnu',
    energie: normaliserEnergieActivationSignal(source.energie || (frontiere && frontiere.energie_activation_effective)),
    chaleur: normaliserChaleurPilotage(source.chaleur, branche.chaleur_experimentale),
    pression: normaliserSignalSimple(source.pression || branche.pression_observee, 'non_disponible'),
    valeur: normaliserValeurMachineSignal(source.valeur || (branche.signaux_attention && branche.signaux_attention.valeur)),
    transversalite: normaliserTransversalite(source.transversalite, domainesParDefaut),
    franchissabilite: normaliserSignalSimple(source.franchissabilite, 'inconnue'),
    preuveAttendue: source.preuve_attendue || '',
    blocage: source.blocage || '',
    pourquoiCeChemin: source.pourquoi_ce_chemin || '',
  };
}

function normaliserChaleurPilotage(source, chaleurBranche) {
  const chaleur = normaliserChaleur(source);
  if (chaleur.chaleur_continue !== null || !chaleurBranche) return chaleur;
  const chaleurContinueBranche = chaleurBranche.chaleur_continue !== undefined && chaleurBranche.chaleur_continue !== null
    ? Number(chaleurBranche.chaleur_continue)
    : chaleurBranche.valeur_continue;
  if (!estNombreFourni(chaleurContinueBranche)) return chaleur;
  const valeurContinue = Number(chaleurContinueBranche);
  return {
    ...chaleur,
    chaleur_continue: valeurContinue,
    valeur_continue: valeurContinue,
    source: chaleur.source || chaleurBranche.source || '',
    nb_commits_correspondants_30j: chaleur.nb_commits_correspondants_30j || chaleurBranche.nb_commits_correspondants_30j || 0,
    nb_repos_correspondants_30j: chaleur.nb_repos_correspondants_30j || chaleurBranche.nb_repos_correspondants_30j || 0,
    derniere_activite_substantielle: chaleur.derniere_activite_substantielle || chaleurBranche.derniere_activite_substantielle || null,
    explication: chaleur.explication || chaleurBranche.explication || '',
    limites: chaleur.limites.length > 0 ? chaleur.limites : liste(chaleurBranche.limites),
  };
}

function normaliserSignalSimple(source, niveauParDefaut) {
  const valeur = source || {};
  return {
    niveau: valeur.niveau || niveauParDefaut,
    valeur: valeur.valeur !== undefined && valeur.valeur !== null ? valeur.valeur : '',
    etat: valeur.etat || '',
    provenance: valeur.provenance || valeur.source || '',
    source: valeur.source || '',
    raisons: liste(valeur.raisons),
    preuves: liste(valeur.preuves),
    incertitudes: liste(valeur.incertitudes),
    limites: liste(valeur.limites),
    dernier_signal: valeur.dernier_signal || null,
    depuis: valeur.depuis || null,
  };
}

function normaliserEnergieActivationSignal(source) {
  const signal = normaliserSignalSimple(source, 'inconnue');
  const machine = normaliserEnergieActivationMachine(source && source.energie_activation_machine_v0);
  const scoreMachine = machine && estNombreFourni(machine.score) ? Number(machine.score) : null;
  return {
    ...signal,
    valeur: scoreMachine !== null ? scoreMachine : signal.valeur,
    score: scoreMachine !== null ? scoreMachine : estNombreFourni(source && source.score) ? Number(source.score) : null,
    certitude: estNombreFourni(source && source.certitude) ? Number(source.certitude) : machine ? machine.certitude : null,
    energie_activation_machine_v0: machine,
    semantique: (source && source.semantique) || '0 = preuve proche; 100 = effort important',
  };
}

function normaliserValeurMachineSignal(source) {
  const signal = normaliserSignalSimple(source, 'inconnue');
  const machine = normaliserValeurMachine(source && source.valeur_machine_v0);
  const scoreMachine = machine && estNombreFourni(machine.score) ? Number(machine.score) : null;
  return {
    ...signal,
    valeur: scoreMachine !== null ? scoreMachine : signal.valeur,
    valeur_continue: scoreMachine !== null
      ? scoreMachine
      : estNombreFourni(source && source.valeur_continue)
      ? Number(source.valeur_continue)
      : null,
    valeur_machine_v0: machine,
  };
}

function normaliserValeurMachine(source) {
  if (!source || typeof source !== 'object') return null;
  const axesSource = source.axes || {};
  const axes = ['impact', 'portee', 'recurrence', 'durabilite', 'levier'].reduce((index, axe) => ({
    ...index,
    [axe]: normaliserAxeValeurMachine(axesSource[axe]),
  }), {});
  return {
    version: source.version || 'valeur_machine_v0',
    score: estNombreFourni(source.score) ? Number(source.score) : null,
    statut: source.statut || 'inconnue',
    niveau: source.niveau || 'inconnue',
    axes,
    valeur_directe: estNombreFourni(source.valeur_directe) ? Number(source.valeur_directe) : null,
    valeur_levier: estNombreFourni(source.valeur_levier) ? Number(source.valeur_levier) : null,
    explication: source.explication || '',
    formule: source.formule || '',
    limites: liste(source.limites),
    sources: source.sources || {},
  };
}

function normaliserEnergieActivationMachine(source) {
  if (!source || typeof source !== 'object') return null;
  const axesSource = source.axes || {};
  const axes = ['distance_preuve', 'complexite_execution', 'dependances_acces', 'coordination_decision']
    .reduce((index, axe) => ({
      ...index,
      [axe]: normaliserAxeEnergieActivation(axesSource[axe]),
    }), {});
  return {
    version: source.version || 'energie_activation_machine_v0',
    etat: source.etat || source.statut || 'inconnue',
    statut: source.statut || source.etat || 'inconnue',
    score: estNombreFourni(source.score) ? Number(source.score) : null,
    certitude: estNombreFourni(source.certitude) ? Number(source.certitude) : null,
    niveau: source.niveau || 'inconnue',
    axes,
    formule: source.formule || '',
    sources: source.sources || {},
    limites: liste(source.limites),
  };
}

function normaliserAxeEnergieActivation(source) {
  const valeur = source || {};
  return {
    score: estNombreFourni(valeur.score) ? Number(valeur.score) : null,
    certitude: estNombreFourni(valeur.certitude) ? Number(valeur.certitude) : null,
    etat: valeur.etat || 'inconnu',
    raisons: liste(valeur.raisons),
    sources: liste(valeur.sources),
  };
}

function normaliserAxeValeurMachine(source) {
  const valeur = source || {};
  return {
    score: estNombreFourni(valeur.score) ? Number(valeur.score) : null,
    certitude: estNombreFourni(valeur.certitude) ? Number(valeur.certitude) : null,
    valeur_attendue: estNombreFourni(valeur.valeur_attendue) ? Number(valeur.valeur_attendue) : null,
    etat: valeur.etat || 'inconnu',
    justification: valeur.justification || '',
    sources: liste(valeur.sources),
  };
}

function normaliserChaleur(source) {
  const valeur = source || {};
  const valeurContinue = estNombreFourni(valeur.chaleur_continue)
    ? Number(valeur.chaleur_continue)
    : valeur.valeur_continue;
  return {
    niveau: valeur.niveau || 'inconnu',
    source: valeur.source || '',
    chaleur_continue: estNombreFourni(valeurContinue) ? Number(valeurContinue) : null,
    valeur_continue: estNombreFourni(valeurContinue) ? Number(valeurContinue) : null,
    nb_commits_correspondants_30j: Number(valeur.nb_commits_correspondants_30j || 0),
    nb_repos_correspondants_30j: Number(valeur.nb_repos_correspondants_30j || 0),
    derniere_activite_substantielle: valeur.derniere_activite_substantielle || null,
    explication: valeur.explication || '',
    limites: liste(valeur.limites),
  };
}

function normaliserRecence(source) {
  const valeur = source || {};
  return {
    niveau: valeur.niveau || 'branche',
    derniere_activite_substantielle: valeur.derniere_activite_substantielle || null,
    source: valeur.source || '',
  };
}

function normaliserTransversalite(source, domainesParDefaut) {
  const valeur = source || {};
  const domaines = liste(valeur.domaines);
  return {
    niveau: valeur.niveau || 'branche',
    nombre_domaines: Number(valeur.nombre_domaines || domaines.length || domainesParDefaut.length || 0),
    domaines: domaines.length > 0 ? domaines : domainesParDefaut,
    source: valeur.source || 'ancrage + domaines_lies',
    interpretation: valeur.interpretation || '',
  };
}



// WEBPACK FOOTER //
// src/components/cockpit/grapheCognitifService.js